import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaChevronDown, FaChevronRight, FaClipboardList, FaFileAlt, FaPaperclip } from "react-icons/fa";
import { BsClipboardCheckFill } from "react-icons/bs";
import { CiLogout } from "react-icons/ci";
import { FiX } from "react-icons/fi";
import Swal from "sweetalert2";

import ChecklistLayout from "../../../Componentes/ChecklistLayout";
import QuestaoResponderCard from "./components/QuestaoResponderCard";
import AnaliseComentarios, {
  AnaliseComentariosTitle,
} from "./components/Analise/AnaliseComentarios";
import api from "../../../Services/api";
import requestPreSignedUrlAws from "../../../Services/requestPreSignedUrlAws";
import { excluirArquivoQuestaoResposta } from "../../../Services/Auditoria/chkQuestaoRespostaArquivo";
import { useOperador } from "../../../Hooks/useOperador";
import { ETipoPermissao } from "../../../Enum/Enum";
import Logo from "../../../Imagens/logoPentagov.jpg";

import "./index.css";

const AuditoriaChecklist = () => {
  const { IDSecao } = useParams();
  const secaoId = Number.isNaN(Number(IDSecao)) ? null : Number(IDSecao);

  const [secao, setSecao] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [questoes, setQuestoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewArquivo, setPreviewArquivo] = useState(null);
  const [previewQuestao, setPreviewQuestao] = useState(null);
  const [previewStatus, setPreviewStatus] = useState("idle");
  const [previewErro, setPreviewErro] = useState("");
  const [previewCarregando, setPreviewCarregando] = useState(false);
  const [questaoSelecionada, setQuestaoSelecionada] = useState(null);
  const [analiseHasComentarios, setAnaliseHasComentarios] = useState(false);
  const [ocultarRespondidas, setOcultarRespondidas] = useState(false);
  const [modoExibicao, setModoExibicao] = useState("ordem");
  const [gruposExpandidos, setGruposExpandidos] = useState({});
  const [statusQuestoesDebounced, setStatusQuestoesDebounced] = useState({});
  const [statusQuestoes, setStatusQuestoes] = useState({});
  const [savingMap, setSavingMap] = useState({});
  const salvandoRespostas = Object.values(savingMap).some(Boolean);
  const [finalizando, setFinalizando] = useState(false);
  const [painelDireitoModo, setPainelDireitoModo] = useState("analises");
  const [arquivosQuestaoSelecionada, setArquivosQuestaoSelecionada] = useState([]);
  const [excluindoAnexoId, setExcluindoAnexoId] = useState(null);
  const [anexosVersion, setAnexosVersion] = useState(0);
  const ignorarResetPainelRef = useRef(false);
  const { permissaoPrincipal, IDOperador, nome } = useOperador();
  const isAdmin = permissaoPrincipal === ETipoPermissao.ADMIN;
  const navigate = useNavigate();

  useEffect(() => {
    if (!secaoId) return;

    const carregarDados = async () => {
      try {
        setLoading(true);
        const [secaoResponse, questoesResponse] = await Promise.all([
          api.get(`/chksecao/${secaoId}`),
          api.get("/chkquestaosecao/secao", {
            params: {
              page: 1,
              limit: 100,
              IDSecao: secaoId,
            },
          }),
        ]);

        const secaoData = secaoResponse.data;
        setSecao(secaoData);
        setQuestoes(questoesResponse.data?.data || []);
      } catch (error) {
        console.error("Erro ao carregar checklist da secao", error);
        setQuestoes([]);
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [secaoId]);

  useEffect(() => {
    if (!secao?.IDCliente) {
      setCliente(null);
      return;
    }

    const carregarCliente = async () => {
      try {
        const response = await api.get(`/cliente/${secao.IDCliente}`);
        setCliente(response.data?.Cliente || null);
      } catch (error) {
        console.error("Erro ao buscar cliente:", error);
        setCliente(null);
      }
    };

    carregarCliente();
  }, [secao?.IDCliente]);

  useEffect(() => {
    setStatusQuestoes({});
    setOcultarRespondidas(false);
    setModoExibicao("ordem");
    setGruposExpandidos({});
    setStatusQuestoesDebounced({});
  }, [secaoId]);

  useEffect(() => {
    if (!ocultarRespondidas) {
      setStatusQuestoesDebounced(statusQuestoes);
      return;
    }

    const timer = setTimeout(() => {
      setStatusQuestoesDebounced(statusQuestoes);
    }, 500);

    return () => clearTimeout(timer);
  }, [ocultarRespondidas, statusQuestoes]);

  useEffect(() => {
    setAnaliseHasComentarios(false);
  }, [questaoSelecionada?.IDQuestao_Secao]);

  useEffect(() => {
    if (ignorarResetPainelRef.current) {
      ignorarResetPainelRef.current = false;
      return;
    }

    setPainelDireitoModo("analises");
    setArquivosQuestaoSelecionada([]);
  }, [questaoSelecionada?.IDQuestao_Secao]);

  useEffect(() => {
    setPreviewArquivo(null);
    setPreviewStatus("idle");
    setPreviewErro("");
    setPreviewCarregando(false);
    setPreviewQuestao(null);
  }, [secaoId]);

  const fecharPreview = () => {
    setPreviewArquivo(null);
    setPreviewStatus("idle");
    setPreviewErro("");
    setPreviewCarregando(false);
    setPreviewQuestao(null);
  };

  const getNomeArquivo = (path = "") => {
    if (!path) return "";
    const partes = String(path).split("/");
    return partes[partes.length - 1] || "";
  };

  const getTipoArquivo = (nome = "", url = "") => {
    const alvo = nome || url || "";
    const extensao = alvo.split(".").pop()?.toLowerCase() || "";
    if (extensao === "pdf") return "pdf";
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(extensao)) {
      return "imagem";
    }
    return "outro";
  };

  const handleAbrirArquivo = async (objeto, questao) => {
    if (!objeto) return;

    setPreviewStatus("loading");
    setPreviewErro("");
    setQuestaoSelecionada(questao);
    setPreviewQuestao(questao);

    if (objeto?.tipo === 2) {
      const nomeDiretorio = objeto.descricao || objeto.path || "";
      setPreviewArquivo({ nome: nomeDiretorio, tipo: "diretorio" });
      setPreviewCarregando(false);
      setPreviewStatus("ready");
      return;
    }

    const bucket = objeto.nomeBucketAws || null;
    const caminho = objeto.caminho || null;
    const nome = objeto.descricao || getNomeArquivo(objeto.path);

    if (!bucket || !caminho) {
      setPreviewStatus("error");
      setPreviewErro(
        "Nao foi possivel identificar o bucket ou caminho do arquivo."
      );
      return;
    }

    const url = await requestPreSignedUrlAws(
      permissaoPrincipal,
      "getObject",
      bucket,
      caminho,
      nome,
      "inline"
    );

    if (!url || typeof url !== "string") {
      setPreviewStatus("error");
      setPreviewErro("Nao foi possivel obter o link do arquivo.");
      return;
    }

    const tipo = getTipoArquivo(nome, url);
    setPreviewArquivo({ url, nome, tipo });
    setPreviewCarregando(tipo !== "outro");
    setPreviewStatus("ready");
  };

  const questoesAtivas = questoes.filter((questao) => questao?.situacao !== 0);
  const questoesExibidas = useMemo(
    () =>
      questoes.filter((questao) =>
        ocultarRespondidas
          ? !statusQuestoesDebounced[questao.IDQuestao_Secao]
          : true,
      ),
    [ocultarRespondidas, questoes, statusQuestoesDebounced],
  );
  const blocosQuestoes = useMemo(() => {
    const mapa = new Map();

    questoesExibidas.forEach((questao) => {
      const idGrupo =
        questao?.IDGrupoQuestao_Secao ??
        questao?.IDGrupoQuestaoAplicado ??
        null;
      const descricaoGrupo = (questao?.grupoDescricao || "").trim();
      const isDireta = !idGrupo;
      const chave = isDireta ? "direta" : `grupo-${idGrupo}`;
      const titulo = isDireta
        ? "Associação direta"
        : descricaoGrupo || `Grupo #${idGrupo}`;

      if (!mapa.has(chave)) {
        mapa.set(chave, {
          chave,
          titulo,
          questoes: [],
        });
      }

      mapa.get(chave).questoes.push(questao);
    });

    return Array.from(mapa.values());
  }, [questoesExibidas]);

  useEffect(() => {
    setGruposExpandidos((anterior) => {
      const proximo = {};
      blocosQuestoes.forEach((bloco) => {
        proximo[bloco.chave] = anterior[bloco.chave] ?? true;
      });
      return proximo;
    });
  }, [blocosQuestoes]);
  const todasAtivasRespondidas = questoesAtivas.every(
    (questao) => statusQuestoes[questao.IDQuestao_Secao],
  );
  const totalAtivas = questoesAtivas.length;
  const totalRespondidas = questoesAtivas.reduce(
    (total, questao) => total + (statusQuestoes[questao.IDQuestao_Secao] ? 1 : 0),
    0,
  );
  const percentualRespondidas = totalAtivas
    ? Math.round((totalRespondidas / totalAtivas) * 100)
    : 0;
  const checklistFinalizado = secao?.situacao === 2;

  const handleSavingChange = useCallback((id, isSaving) => {
    setSavingMap((prev) =>
      prev[id] === isSaving ? prev : { ...prev, [id]: isSaving },
    );
  }, []);

  const handleStatusChange = useCallback((id, respondeu) => {
    setStatusQuestoes((prev) =>
      prev[id] === respondeu ? prev : { ...prev, [id]: respondeu },
    );
  }, []);

  const handleExibirArquivosQuestao = useCallback((questao, arquivos) => {
    if (!questao) return;

    if (
      Number(questaoSelecionada?.IDQuestao_Secao) !== Number(questao.IDQuestao_Secao)
    ) {
      ignorarResetPainelRef.current = true;
      setQuestaoSelecionada(questao);
    }

    setArquivosQuestaoSelecionada(arquivos || []);
    setPainelDireitoModo("anexos");
  }, [questaoSelecionada?.IDQuestao_Secao]);

  const handleAnexosChange = useCallback((questao, arquivos) => {
    if (
      painelDireitoModo !== "anexos" ||
      Number(questaoSelecionada?.IDQuestao_Secao) !== Number(questao?.IDQuestao_Secao)
    ) {
      return;
    }

    setArquivosQuestaoSelecionada(arquivos || []);
  }, [painelDireitoModo, questaoSelecionada?.IDQuestao_Secao]);

  const handleExcluirArquivoQuestao = useCallback(async (anexo) => {
    if (!anexo?.IDChkQuestaoResposta_Arquivo || !permissaoPrincipal) return;

    const confirmacao = await Swal.fire({
      title: "Excluir anexo?",
      text: `Confirma excluir o arquivo "${anexo.nome}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      cancelButtonColor: "var(--color-btnCancelar-danger)",
      customClass: {
        cancelButton: "swal-cancel-danger",
      },
    });

    if (!confirmacao.isConfirmed) return;

    try {
      setExcluindoAnexoId(anexo.IDChkQuestaoResposta_Arquivo);
      await excluirArquivoQuestaoResposta({
        IDChkQuestaoResposta_Arquivo: anexo.IDChkQuestaoResposta_Arquivo,
        tipoPermissao: permissaoPrincipal,
      });

      setArquivosQuestaoSelecionada((prev) =>
        prev.filter(
          (item) =>
            Number(item.IDChkQuestaoResposta_Arquivo) !==
            Number(anexo.IDChkQuestaoResposta_Arquivo)
        )
      );
      setAnexosVersion((prev) => prev + 1);

      await Swal.fire({
        icon: "success",
        title: "Anexo excluído",
        text: "O arquivo foi removido com sucesso.",
      });
    } catch (error) {
      console.error("Erro ao excluir anexo", error);
      const mensagem =
        error?.response?.data?.errors?.default ||
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível excluir o anexo.";

      await Swal.fire({
        icon: "error",
        title: "Falha ao excluir",
        text: mensagem,
      });
    } finally {
      setExcluindoAnexoId(null);
    }
  }, [permissaoPrincipal]);

  const renderQuestaoItem = (questao) => (
    <li
      key={questao.IDQuestao_Secao}
      className={`auditoria-checklist-item ${questaoSelecionada?.IDQuestao_Secao ===
        questao.IDQuestao_Secao
        ? "is-selected"
        : ""
        }`}
      onClick={() => setQuestaoSelecionada(questao)}
    >
      <QuestaoResponderCard
        questao={questao}
        onAbrirArquivo={handleAbrirArquivo}
        onExibirArquivos={handleExibirArquivosQuestao}
        onAnexosChange={handleAnexosChange}
        anexosVersion={anexosVersion}
        readOnly={checklistFinalizado}
        isSelected={questaoSelecionada?.IDQuestao_Secao === questao.IDQuestao_Secao}
        onSavingChange={handleSavingChange}
        onStatusChange={handleStatusChange}
      />
    </li>
  );

  const handleFinalizarChecklist = async () => {
    if (!secao?.IDSecao || finalizando) return;

    if (!todasAtivasRespondidas) {
      await Swal.fire({
        title: "Checklist incompleto",
        text: "Existem questões ativas pendentes. Responda todas antes de finalizar.",
        icon: "warning",
      });
      return;
    }

    const confirmacao = await Swal.fire({
      title: "Finalizar checklist?",
      text: "Essa ação é irreversível. Deseja continuar?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, finalizar",
      cancelButtonText: "Cancelar",
      cancelButtonColor: "var(--color-btnCancelar-danger)",
      customClass: {
        cancelButton: "swal-cancel-danger",
      },
    });

    if (!confirmacao.isConfirmed) return;

    try {
      setFinalizando(true);
      const payload = {
        IDSecao: secao.IDSecao,
        IDCliente: secao.IDCliente,
        descricao: secao.descricao,
        ordem: secao.ordem ?? null,
        situacao: 2,
      };
      await api.put(`/chksecao/${secao.IDSecao}`, payload);
      setSecao((prev) => (prev ? { ...prev, situacao: 2 } : prev));
      await Swal.fire(
        "Checklist finalizado",
        "O checklist foi finalizado com sucesso.",
        "success",
      );
    } catch (error) {
      console.error("Erro ao finalizar checklist", error);
      await Swal.fire(
        "Erro",
        "Nao foi possivel finalizar o checklist.",
        "error",
      );
    } finally {
      setFinalizando(false);
    }
  };

  return (
    <ChecklistLayout
      topContent={
        <>
          <div className="mb-4 auditoria-checklist-topo">
            <div className="auditoria-checklist-topo-inner">
              <div className="auditoria-checklist-topo-hero">
                <div className="auditoria-checklist-topo-brand">
                  <div className="auditoria-checklist-topo-logo">
                    <img className="imgLogo" src={Logo} alt="Logo Pentagov" />
                  </div>
                  <div className="auditoria-checklist-topo-text">
                    <span className="auditoria-checklist-topo-eyebrow">Checklist de auditoria</span>
                    <div className="auditoria-checklist-topo-heading-row">
                      <h1>
                        {cliente?.nome ? `${cliente.nome} - ${cliente?.uf || ""}` : "Checklist de Auditoria"}
                      </h1>
                      <span className="auditoria-checklist-topo-arrow">|</span>
                      <h2>{secao?.descricao || "Carregando secao..."}</h2>
                    </div>
                  </div>
                </div>

              <button
                type="button"
                className="auditoria-checklist-topo-back"
                onClick={() => navigate("/Menu")}
              >
                  <CiLogout aria-hidden="true" />
                Menu Principal
              </button>
              </div>
            </div>
          </div>
        </>
      }
      leftContent={{
        title: (
          <div className="auditoria-checklist-preview-header">
            <h3>
              <FaFileAlt className="auditoria-checklist-title-icon" />
              Visualizador de Arquivos
            </h3>
            <span className="auditoria-checklist-preview-subtitle">
              {previewQuestao?.titulo &&
                `Questão: #${previewQuestao.IDQuestao} - ${previewQuestao.titulo}`}
            </span>
          </div>
        ),
        actions: (
          !!previewArquivo && (
            <button
              type="button"
              className="auditoria-checklist-preview-close"
              onClick={fecharPreview}
              aria-label="Fechar visualizacao do arquivo"
              title="Fechar visualizacao"
            >
              <FiX />
            </button>
          )
        ),
        content: (
          <div className="auditoria-checklist-preview">
            <div className="auditoria-checklist-preview-body">
              {previewStatus === "idle" && (
                <span>Selecione um arquivo da questao para visualizar aqui.</span>
              )}
              {previewStatus === "loading" && (
                <span>Carregando arquivo...</span>
              )}
              {previewStatus === "error" && <span>{previewErro}</span>}
              {previewStatus === "ready" && previewArquivo && (
                <div className="auditoria-checklist-preview-embed">
                  {previewCarregando && (
                    <div
                      className="auditoria-checklist-preview-overlay"
                      aria-live="polite"
                    >
                      <div className="auditoria-checklist-preview-spinner" />
                      <span>Carregando visualizacao...</span>
                    </div>
                  )}
                  {previewArquivo.tipo === "diretorio" && (
                    <span>{`Diretório: ${previewArquivo.nome}`}</span>
                  )}
                  {previewArquivo.tipo === "pdf" && (
                    <iframe
                      title="Arquivo"
                      src={`${previewArquivo.url}#toolbar=1&navpanes=1&scrollbar=1&zoom=page-fit`}
                      width="100%"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      allowFullScreen
                      onLoad={() => setPreviewCarregando(false)}
                      onError={() => {
                        setPreviewCarregando(false);
                        setPreviewStatus("error");
                        setPreviewErro("Nao foi possivel carregar o arquivo.");
                      }}
                    />
                  )}
                  {previewArquivo.tipo === "imagem" && (
                    <img
                      src={previewArquivo.url}
                      alt={previewArquivo.nome || "Arquivo"}
                      loading="lazy"
                      onLoad={() => setPreviewCarregando(false)}
                      onError={() => {
                        setPreviewCarregando(false);
                        setPreviewStatus("error");
                        setPreviewErro("Nao foi possivel carregar a imagem.");
                      }}
                    />
                  )}
                  {previewArquivo.tipo === "outro" && (
                    <a href={previewArquivo.url} target="_blank" rel="noreferrer">
                      Abrir arquivo
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      }}
      middleContent={{
        title: (
          <div className="auditoria-checklist-questoes-header">
            <div className="auditoria-checklist-questoes-header-top">
              <h3>
                <FaClipboardList className="auditoria-checklist-title-icon" />
                Checklist da Seção
              </h3>
            </div>
            <div className="auditoria-checklist-questoes-header-bottom">
              {secao && (
                <span className="auditoria-checklist-questoes-subtitle">
                  {secao.descricao}
                </span>
              )}
              <span className="auditoria-checklist-questoes-percent">
                {salvandoRespostas ? "Salvando..." : `${percentualRespondidas}%`}
              </span>
            </div>
          </div>
        ),
        progress: percentualRespondidas,
        filters: (
          <div className="auditoria-checklist-filtros">
            <div className="auditoria-checklist-filtros-left">
              <div className="auditoria-checklist-exibicao">
                <span className="auditoria-checklist-exibicao-label">Exibição:</span>
                <div className="auditoria-checklist-modo">
                  <button
                    type="button"
                    className={`auditoria-checklist-modo-btn ${modoExibicao === "ordem" ? "is-active" : ""
                      }`}
                    onClick={() => setModoExibicao("ordem")}
                  >
                    Ordem da seção
                  </button>
                  <button
                    type="button"
                    className={`auditoria-checklist-modo-btn ${modoExibicao === "origem" ? "is-active" : ""
                      }`}
                    onClick={() => setModoExibicao("origem")}
                  >
                    Por origem
                  </button>
                </div>
              </div>
              <label className="auditoria-checklist-filtro">
                <input
                  type="checkbox"
                  checked={ocultarRespondidas}
                  onChange={(event) => setOcultarRespondidas(event.target.checked)}
                  onMouseDown={() => {
                    const active = document.activeElement;
                    if (active && typeof active.blur === "function") {
                      active.blur();
                    }
                  }}
                  disabled={salvandoRespostas}
                />
                Ocultar respondidas
              </label>
            </div>
            {!loading &&
              questoesAtivas.length > 0 &&
              todasAtivasRespondidas &&
              !checklistFinalizado &&
              isAdmin && (
                <button
                  type="button"
                  className="auditoria-checklist-finalizar"
                  onClick={handleFinalizarChecklist}
                  disabled={loading || finalizando}
                >
                  <BsClipboardCheckFill aria-hidden="true" />
                  {finalizando ? "Finalizando..." : "Finalizar Checklist"}
                </button>
              )}
          </div>
        ),
        content: (
          <div className="auditoria-checklist-questoes">

            {loading && (
              <p className="auditoria-checklist-status">Carregando...</p>
            )}
            {!loading && !questoes.length && (
              <p className="auditoria-checklist-status">
                Nenhuma questao encontrada.
              </p>
            )}
            {!loading && questoes.length > 0 && (
              <>
                {!questoesExibidas.length && (
                  <p className="auditoria-checklist-status">
                    Nenhuma questão pendente para os filtros selecionados.
                  </p>
                )}
                {!!questoesExibidas.length && (
                  <ul className="auditoria-checklist-list">
                    {modoExibicao === "ordem" &&
                      questoesExibidas.map((questao) => renderQuestaoItem(questao))}

                    {modoExibicao === "origem" &&
                      blocosQuestoes.map((bloco) => {
                        const expandido = gruposExpandidos[bloco.chave] ?? true;

                        return (
                          <li key={bloco.chave} className="auditoria-checklist-group-block">
                            <button
                              type="button"
                              className="auditoria-checklist-group-header"
                              onClick={() =>
                                setGruposExpandidos((anterior) => ({
                                  ...anterior,
                                  [bloco.chave]: !expandido,
                                }))
                              }
                              aria-expanded={expandido}
                            >
                              <span className="auditoria-checklist-group-toggle-icon">
                                {expandido ? <FaChevronDown /> : <FaChevronRight />}
                              </span>
                              <span className="auditoria-checklist-group-label">Origem</span>
                              <strong className="auditoria-checklist-group-title">
                                {bloco.titulo}
                              </strong>
                              <span className="auditoria-checklist-group-count">
                                {bloco.questoes.length} {bloco.questoes.length === 1 ? "questão" : "questões"}
                              </span>
                            </button>
                            {expandido && (
                              <ul className="auditoria-checklist-group-items">
                                {bloco.questoes.map((questao) => renderQuestaoItem(questao))}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                  </ul>
                )}
              </>
            )}
          </div>
        ),
      }}
      rightContent={{
        title: (
          painelDireitoModo === "anexos" ? (
            <div className="auditoria-checklist-comentarios-header">
              <h3>
                <FaPaperclip className="auditoria-checklist-title-icon" />
                Arquivos Anexados
              </h3>
              <span className="auditoria-checklist-comentarios-subtitle">
                {questaoSelecionada?.titulo
                  ? `Questao: #${questaoSelecionada.IDQuestao} - ${questaoSelecionada.titulo}`
                  : "Selecione uma questao"}
              </span>
            </div>
          ) : (
            <AnaliseComentariosTitle questaoSelecionada={questaoSelecionada} />
          )
        ),
        content: (
          painelDireitoModo === "anexos" ? (
            <div className="auditoria-checklist-anexos-panel">
              {!questaoSelecionada && (
                <p className="auditoria-comentario-placeholder">
                  Selecione uma questao para ver os arquivos anexados.
                </p>
              )}
              {!!questaoSelecionada && !arquivosQuestaoSelecionada.length && (
                <p className="auditoria-comentario-placeholder">
                  Nenhum arquivo anexado para esta questao.
                </p>
              )}
              {!!questaoSelecionada && !!arquivosQuestaoSelecionada.length && (
                <ul className="auditoria-checklist-anexos-list">
                  {arquivosQuestaoSelecionada.map((anexo) => (
                    <li
                      key={anexo.IDChkQuestaoResposta_Arquivo}
                      className="auditoria-checklist-anexos-item"
                    >
                      <div className="auditoria-checklist-anexos-item-main">
                        <strong title={anexo.nome}>{anexo.nome}</strong>
                        <span>{anexo.diretorio}</span>
                      </div>
                      <div className="auditoria-checklist-anexos-item-actions">
                        <button
                          type="button"
                          className="chk-btn-abrir chk-btn-abrir-anexo"
                          onClick={() =>
                            handleAbrirArquivo(
                              {
                                tipo: 1,
                                nomeBucketAws: anexo.nomeBucketAws,
                                caminho: anexo.caminho,
                                descricao: anexo.nome,
                                path: `${anexo.diretorio} / ${anexo.nome}`,
                              },
                              questaoSelecionada,
                            )
                          }
                        >
                          Abrir
                        </button>
                        <button
                          type="button"
                          className="chk-btn-excluir-anexo"
                          onClick={() => handleExcluirArquivoQuestao(anexo)}
                          disabled={
                            excluindoAnexoId === anexo.IDChkQuestaoResposta_Arquivo
                          }
                        >
                          {excluindoAnexoId === anexo.IDChkQuestaoResposta_Arquivo
                            ? "Excluindo..."
                            : "Excluir"}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <AnaliseComentarios
              questaoSelecionada={questaoSelecionada}
              onHasAnalisesChange={setAnaliseHasComentarios}
            />
          )
        ),
        hasAlert: painelDireitoModo === "anexos" ? false : analiseHasComentarios,
      }}
    />
  );
};

export default AuditoriaChecklist;
